import { useState } from "react";
import { IconArrowLeft, IconEdit, IconPlus, IconReportAnalytics, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, EmptyState, Heading, IconButton, Loader, Text } from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { useConfirmDialog } from "@/lib/confirm-dialog-store";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { ReporteDefaultDto } from "@/features/reporteria/services/reporteria.api";
import { defEliminar } from "../services/admin-reporteria.api";
import type { ConfigResumen } from "../services/admin-reporteria.api";
import { usePredefinidosDeConfig } from "../hooks/use-def-builder";
import { PredefinidosBuilder } from "./PredefinidosBuilder";

export function PredefinidosManager({ config, onSalir }: { config: ConfigResumen; onSalir: () => void }) {
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const lista = usePredefinidosDeConfig(config.id);
  const [editando, setEditando] = useState<{ predefinido: ReporteDefaultDto | null } | null>(null);

  const borrar = useMutation({
    mutationFn: defEliminar,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repAdminPredefinidos(config.id) });
      toastExito({ title: "Predefinido eliminado" });
    },
    onError: toastErrorApi,
  });

  async function confirmarEliminar(p: ReporteDefaultDto) {
    const ok = await confirm({
      title: "Eliminar predefinido",
      message: (
        <>
          ¿Eliminar <b>{p.nombre}</b>? Dejará de aparecer para los usuarios de esta configuración.
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) borrar.mutate(p.id);
  }

  if (editando) {
    return (
      <PredefinidosBuilder idConfig={config.id} predefinido={editando.predefinido} onSalir={() => setEditando(null)} />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton aria-label="Volver" variant="subtle" onClick={onSalir}>
            <IconArrowLeft size={18} />
          </IconButton>
          <div>
            <Heading order={3}>Reportes predefinidos</Heading>
            <Text size="sm" c="dimmed">
              Configuración: {config.nombre}
            </Text>
          </div>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditando({ predefinido: null })}>
          Nuevo predefinido
        </Button>
      </div>

      {lista.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : !lista.data || lista.data.length === 0 ? (
        <EmptyState
          icon={IconReportAnalytics}
          title="Sin reportes predefinidos"
          description="Crea uno para dar a los usuarios de esta configuración un punto de partida."
        />
      ) : (
        <div className="space-y-2">
          {lista.data.map((p) => (
            <Card key={p.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <span className="truncate font-medium">{p.nombre}</span>
                {p.descripcion && (
                  <Text size="xs" c="dimmed" className="truncate">
                    {p.descripcion}
                  </Text>
                )}
              </div>
              <IconButton aria-label="Editar" variant="light" onClick={() => setEditando({ predefinido: p })}>
                <IconEdit size={16} />
              </IconButton>
              <IconButton aria-label="Eliminar" variant="subtle" onClick={() => confirmarEliminar(p)}>
                <IconTrash size={16} />
              </IconButton>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
